import { Component, Inject, Input, Output, EventEmitter } from "@angular/core";
import { combineLatest, forkJoin, Observable, of, Subject } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { SubjectRelay } from "../../../../shared/commands/subject-relay.class";
import { MatTableDataSource } from "@angular/material/table";

import { ButtonActions } from '../../../../app.constants';
import { DocumentPaButton } from '../../entities/document-pa-button';
import { DocumentPaService } from "../../services/document-pa-service";
import { DocumentPersonneAvertie, EtatDocumentPa } from "../../entities/document-pa.entity";

import { EntrepriseExterieureEntity } from "../../entities/entreprise-exterieure.entity";
import { EntrepriseTLR } from "../../../entreprises/entities/entreprise-tlr.entity";
import { UpdateStepEntrepriseOrder } from "../../entities/document-pa-update-step-entreprise-order.entity";
import { SignatairePaEntity } from "../../entities/signataire-pa.entity";
import { IntervenantService } from "../../../humain/intervenant/services/intervenant.service";
import { EntrepriseService } from "../../../entreprises/services/entreprise.service";
import { IntervenantTLR } from "../../../humain/intervenant/entities/intervenant-tlr.entity";
import { UpdateStepValidationOrder } from "../../entities/document-pa-update-step-validation-order.entity";

import { StepAction } from "../../entities/step-action-entity";

@Component({
    selector: 'document-pa-validation-signature',
    templateUrl: 'document-pa-validation-signature.html',
    styleUrls: ['document-pa-validation-signature.css'],
})

export class DocumentPaValidationSignatureComponent {

    @Output() actionRequested = new EventEmitter<StepAction>();

    euEeData?: UpdateStepEntrepriseOrder;
    currentPaInstance!: DocumentPersonneAvertie;

    actionButtons: DocumentPaButton[] = [
        new DocumentPaButton(ButtonActions.PREVIOUS, "Back", "btn btn-primary"),
        new DocumentPaButton(ButtonActions.CANCEL, "Liste des PA", "btn btn-danger"),
        new DocumentPaButton(ButtonActions.SAVE, "Sauvegarder maintenant", "btn btn-warning"),
        new DocumentPaButton(ButtonActions.EXPORT, "Export", "btn btn-primary"),
    ];

    entreprises: EntrepriseTLR[] = [];
    intervenantsEu: IntervenantTLR[] = [];
    intervenantsEe: IntervenantTLR[] = [];

    entrepriseUtilisatrice!: EntrepriseTLR;
    selectedEntrepriseForIntervenantDialog!: EntrepriseTLR | null;

    personnesPouvantSigner: SignatairePaEntity[] = [];
    signataires: MatTableDataSource<SignatairePaEntity> = new MatTableDataSource<SignatairePaEntity>();
    gererIntervenantsCommands = new Subject<[SignatairePaEntity[], SignatairePaEntity[]]>();

    generationCommands: Subject<number> = new Subject<number>();

    displayedColumnsSignataires: string[] = ['nom', 'prenom', 'entreprise', 'raisonSociale', 'email', 'fonction'];

    private currentPa$ = new SubjectRelay<DocumentPersonneAvertie>();
    entreprisesExterieures: Map<number, EntrepriseExterieureEntity> = new Map<number, EntrepriseExterieureEntity>();
    eeIsIntervenant: boolean = false;

    signataireDialogId !: string;
    generationPaDialogId !: string;
	
	private currentPaValue?: DocumentPersonneAvertie;

    @Input() set currentPa(obs: Observable<DocumentPersonneAvertie> | undefined) {
        this.currentPa$.registerSource(obs);
    }

    constructor(
        @Inject(IntervenantService) intervenantService: IntervenantService,
        @Inject(EntrepriseService) entrepriseService: EntrepriseService,
        @Inject(DocumentPaService) private documentPaService: DocumentPaService) {

        this.currentPa$.subscribe((currentPa) => {
            if (currentPa) {

                this.currentPaInstance = new DocumentPersonneAvertie();
                this.currentPaInstance.id = currentPa.id;
				this.currentPaInstance.etat = currentPa.etat;
                this.currentPaInstance.lastCompletedStep = currentPa.lastCompletedStep;
                this.currentPaInstance.categorie = currentPa.categorie;
				
                const euEeData$ = this.documentPaService.getStepEntrepriseData(currentPa.id);
                let intervenants$ = intervenantService.getIntervenantsTLRList();
                let entreprises$ = entrepriseService.getActiveEntreprisesTLRList();
                let stepData$ = this.documentPaService.getValidationData(
					currentPa.duplicatedId ? currentPa.duplicatedId : currentPa.id
				);

                this.signataireDialogId = 'signataireDialog' + currentPa.categorie;
                this.generationPaDialogId = 'generationPaDialog' + currentPa.categorie;

				this.currentPaValue = currentPa;
				
                const data$ = combineLatest([euEeData$, stepData$, entreprises$, intervenants$]).pipe(
                    mergeMap(([euEeData, stepData, allEntreprises, allIntervenants]) =>
                        forkJoin({
                            euEeData: of(euEeData),
                            stepData: of(stepData),
                            allEntreprises: of(allEntreprises),
                            allIntervenants: of(allIntervenants)

                        })
                    )
                );

                data$.subscribe(({ euEeData, stepData, allEntreprises, allIntervenants }) => {
                    this.euEeData = euEeData;

                    if (stepData?.signataires?.length) {
                        const enrichedSignataires =
                            this.matchSignatairesWithEntrepriseData(
                                stepData.signataires,
                                allIntervenants,
                                allEntreprises
                            );

                        this.signataires = new MatTableDataSource<SignatairePaEntity>(
                            enrichedSignataires
                        );

                        if (this.euEeData?.idResponsableEe) {
                            this.eeIsIntervenant = enrichedSignataires.some(
                                s => s.id === this.euEeData!.idResponsableEe
                            );
                        }

                    } else {
                        this.signataires = new MatTableDataSource<SignatairePaEntity>([]);
                        this.eeIsIntervenant = false;
                    }


                    if (!this.euEeData) return;

                    this.entreprises = [];
                    this.intervenantsEu = [];
                    this.intervenantsEe = [];
					
					const idEntrepriseEE = this.euEeData.idEntrepriseEe;
                    const entreprise = allEntreprises.find(ent => ent.id === idEntrepriseEE);
                    if (entreprise) {
                        this.entreprises.push(entreprise);
                    }

                    this.intervenantsEu = allIntervenants.filter(intervenant => intervenant.entrepriseId === this.euEeData?.idEntrepriseEu);
                    this.intervenantsEe = allIntervenants.filter(intervenant => intervenant.entrepriseId === this.euEeData?.idEntrepriseEe);
                });

            }
        });
    }

    openDialogIntervenants = () => {
        let filteredSignataire: SignatairePaEntity[] = [];
        const selected = this.getSelectedSignataireKeys();

        const mapAndFilter = (intervenants: IntervenantTLR[], entreprise: EntrepriseTLR) =>
            intervenants
                .filter(i =>
                    !selected.some(
                        s =>
                            s.id === i.id &&
                            s.nature === 'intervenant' &&
                            s.idEntreprise === entreprise.id
                    )
                )
                .map(i => this.mapIntervenantToSignataire(i, entreprise));


        if (!this.selectedEntrepriseForIntervenantDialog) {
            const eeEntreprise = this.entreprises.find(e => e.id === this.euEeData?.idEntrepriseEe);
            if (eeEntreprise) {
                filteredSignataire.push(...mapAndFilter(this.intervenantsEe, eeEntreprise));
            }
        } else {
            const entreprise = this.selectedEntrepriseForIntervenantDialog;
            filteredSignataire = mapAndFilter(this.intervenantsEe, entreprise);
        }
        filteredSignataire = filteredSignataire.filter($ => this.signataires.data.map(s => s.id).indexOf($.id) < 0);
        // Remove EE responsable from LEFT when checkbox is checked
        if (this.eeIsIntervenant && this.euEeData?.idResponsableEe) {
            filteredSignataire = filteredSignataire.filter(
                s => s.id !== this.euEeData!.idResponsableEe
            );
        }
        this.gererIntervenantsCommands.next([
            filteredSignataire.sort((a, b) => a.nom.localeCompare(b.nom)),
            this.signataires.data.sort((a, b) => a.nom.localeCompare(b.nom))
        ]);
    };

    private mapIntervenantToSignataire(
        intervenant: IntervenantTLR,
        entreprise: EntrepriseTLR
    ): SignatairePaEntity {
        return {
            id: intervenant.id,
            nom: intervenant.nom,
            prenom: intervenant.prenom ?? '',

            idEntreprise: entreprise.id,
            nomEntreprise: entreprise.nom,
            raisonSociale: entreprise.nom,

            fonction: intervenant.cadreEmploi ?? '',
            email: intervenant.email ?? '',

            hasSigned: false,
            hasSignedEnvelope: false,
            sentToDocusign: false,
            emailStyle: '',
            envelopeId: '',
            envelopeIndex: -1,

            nature: 'intervenant',
            isIntervenant: true,
            updatable: false,
            docusignRecipientId: ''
        };
    }

    private getSelectedSignataireKeys() {
        return this.signataires.data.map(s => ({
            id: s.id,
            nature: s.nature,
            idEntreprise: s.idEntreprise
        }));
    }

    onIntervenantsSelected(data: [SignatairePaEntity[], SignatairePaEntity[]]) {
        let intervenantsLies = data[1];
        let newSource: SignatairePaEntity[] = [];
        if (intervenantsLies !== undefined) {
            intervenantsLies.forEach((value: SignatairePaEntity) => newSource.push(value));
        }
        this.signataires = new MatTableDataSource<SignatairePaEntity>(newSource);

        if (this.euEeData?.idResponsableEe) {
            this.eeIsIntervenant = newSource.some(
                s => s.id === this.euEeData!.idResponsableEe
            );
        }
    }

    private buildEeResponsableSignataire(): SignatairePaEntity {
        if (!this.euEeData) {
            throw new Error('EU/EE data not loaded');
        }

        const eeEntreprise = this.entreprises.find(
            e => e.id === this.euEeData!.idEntrepriseEe
        );

        return {
            id: this.euEeData.idResponsableEe!,
            nom: this.euEeData.nomPrenomResponsableEe?.split(' ')?.[0] ?? '',
            prenom: this.euEeData.nomPrenomResponsableEe?.split(' ')?.slice(1).join(' ') ?? '',

            idEntreprise: eeEntreprise?.id ?? -1,
            nomEntreprise: eeEntreprise?.nom ?? '',
            raisonSociale: eeEntreprise?.nom ?? '',

            email: this.euEeData.emailResponsableEe ?? '',
            fonction: this.euEeData.fonctionResponsableEe ?? '',

            hasSigned: false,
            hasSignedEnvelope: false,
            sentToDocusign: false,
            emailStyle: '',
            envelopeId: '',
            envelopeIndex: -1,

            nature: 'ee',
            isIntervenant: true,
            updatable: false,
            docusignRecipientId: ''
        };
    }

    eeIsIntervenantChange(): void {
        if (!this.euEeData?.idResponsableEe) {
            return;
        }

        let current = [...this.signataires.data];
        const eeId = this.euEeData.idResponsableEe;

        if (this.eeIsIntervenant) {
            if (!current.some(s => s.id === eeId)) {
                current.push(this.buildEeResponsableSignataire());
            }
        } else {
            current = current.filter(s => s.id !== eeId);
        }

        this.signataires = new MatTableDataSource(current);
    }


    performAction(requestedAction: ButtonActions): void {
        switch (requestedAction) {

            case ButtonActions.CANCEL:
            case ButtonActions.RETURN_TO_LIST:
                this.actionRequested.emit(new StepAction(null, ButtonActions.RETURN_TO_LIST));
                break;

            case ButtonActions.PREVIOUS: {
                this.actionRequested.emit(new StepAction(this.currentPaInstance, ButtonActions.PREVIOUS));
                break;
            }

            case ButtonActions.SAVE:
            case ButtonActions.SAVE_DRAFT: {
                const payload = this.buildUpdateSignatairesPayload();

                this.documentPaService.updateValidationStep(payload).subscribe({
                    next: (response) => {
                        if (this.currentPaInstance) {
                            if (this.currentPaInstance.lastCompletedStep < 8) {
                                this.currentPaInstance.lastCompletedStep = 8;
                            }
                        }
                        this.actionRequested.emit(
                            new StepAction(null, ButtonActions.RETURN_TO_LIST)
                        );
                    },
                    error: (err) => {
                        console.error('Erreur lors de la sauvegarde des informations du step Validation signature, erreur: ', err);
                    }
                });

                break;
            }

            case ButtonActions.EXPORT: {
                const payload = this.buildUpdateSignatairesPayload();

                this.documentPaService.updateValidationStep(payload).subscribe({
                    next: () => {

                        if (this.currentPaInstance) {
                            if (this.currentPaInstance.lastCompletedStep < 8) {
                                this.currentPaInstance.lastCompletedStep = 8;
                            }
                        }

                        if (this.currentPaInstance?.id) {
                            this.generationCommands.next(this.currentPaInstance.id);
                        }
                    },
                    error: (err) => {
                        console.error(
                            'Erreur lors de la sauvegarde avant export, erreur: ',
                            err
                        );
                    }
                });

                break;
            }

            default:
                // rien à faire ici
                break;
        }
    }

    private buildUpdateSignatairesPayload(): UpdateStepValidationOrder {
        const payload = new UpdateStepValidationOrder();

        payload.id = this.currentPaInstance.id;

        payload.signataires = this.signataires.data.map(s => ({
            id: s.id,
            email: s.email,
            fonction: s.fonction
        } as SignatairePaEntity));

        return payload;
    }

    private matchSignatairesWithEntrepriseData(
        signataires: SignatairePaEntity[],
        allIntervenants: IntervenantTLR[],
        allEntreprises: EntrepriseTLR[]
    ): SignatairePaEntity[] {

        return signataires.map(signataire => {
            const intervenant = allIntervenants.find(i => i.id === signataire.id);

            if (!intervenant) {
                return signataire;
            }

            const entreprise = allEntreprises.find(
                e => e.id === intervenant.entrepriseId
            );

            if (!entreprise) {
                return signataire;
            }

            return {
                ...signataire,
                idEntreprise: entreprise.id,
                nomEntreprise: entreprise.nom,
                raisonSociale: entreprise.nom
            };
        });
    }
	
	private updateExportButtonState(): void {
	    const exportButton = this.actionButtons.find(
	        b => b.action === ButtonActions.EXPORT
	    );

	    if (exportButton) {
	        exportButton.disabled =
	            this.currentPaValue?.etat === EtatDocumentPa.BROUILLON;
	    }
	}
}
